import { useState, useLayoutEffect, useRef, useEffect } from "react"

import { useNavigate } from "react-router-dom"
import { signInService } from "./auth-service"
import { redirectAfterLogin } from "./post-login-redirect"
import { useSendPasswordResetEmailMutation } from "@features/admin/admin-slice"
import { useVerifyEmailMutation } from "./auth-slice"
import { decrypt } from "../helpers"

import { fetchUser } from "@router/routes/protected-routes"

import { Formik, Form, ErrorMessage } from "formik"
import * as Yup from "yup"
import { PageTitle } from "../components/page-title"
import { ErrorBox } from "../components/error-box"
import { PasswordInput } from "../components/password-input"
import { Modal } from "../components/modal"
import { MarkDown } from "../components/markdown"
import { ExternalPage } from "../components/external-page"
import LoadingButton from "@mui/lab/LoadingButton"

import Button from "@mui/material/Button"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import Divider from "@mui/material/Divider"
import GoogleIcon from "@mui/icons-material/Google"

import { LoginErrorProps, FormProps } from "./auth.types"
import { userRoles, finapiRole } from "@router/constants"
import { useGetReactNativePayload } from "@hooks/index"
import { getUrlRouteAndParams } from "@app/helpers"

declare const window: any

const NONE = "none"
const USER_DISABLED = "user-disabled"
const NOT_AUTH = "not-authenticated"
const WRONG_EMAIL_OR_PASSWORD = "wrong-email-or-password"
const EMAIL_NOT_FOUND = "email-not-found"

const errorMessages = {
  [WRONG_EMAIL_OR_PASSWORD]: {
    code: WRONG_EMAIL_OR_PASSWORD,
    message: "Wrong or invalid email/password",
  },
  [EMAIL_NOT_FOUND]: {
    code: EMAIL_NOT_FOUND,
    message: "Sorry, we can't find that email ",
  },
  [USER_DISABLED]: {
    code: USER_DISABLED,
    message: `Your account is disabled, contact support`,
  },
  [NOT_AUTH]: {
    code: NOT_AUTH,
    message: `You're logged out or not authenticated`,
  },
  [NONE]: { code: NONE, message: "" },
}

export const Login = (authProps: any) => {
  const [sendPasswordResetEmail] = useSendPasswordResetEmailMutation()
  const [verifyEmail] = useVerifyEmailMutation()

  const navigate = useNavigate()

  const [showOnboarding, setShowOnboarding] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [loginError, setLoginError] = useState<LoginErrorProps>()
  const [resetError, setResetError] = useState<LoginErrorProps>()
  const [loading, setLoading] = useState(false)
  const [pageShouldLoad, setPageShouldLoad] = useState(false)
  const [disableTimeout, setDisableTimeout] = useState(0)
  const intervalRef = useRef<NodeJS.Timer | null>(null)

  const isMobileApp = !!window.ReactNativeWebView

  // if it's google login & native then use token to validate user
  const handleNativeGoogleLogin = async (url: string) => {
    const { params } = getUrlRouteAndParams(url)
    const token = params.g
    if (window.ReactNativeWebView && token) {
      const decryptedToken = decrypt(token)
      if (decryptedToken) {
        await loginFn({ email: decryptedToken })
      }
    }
  }

  useGetReactNativePayload("login_link", handleNativeGoogleLogin)

  // show or hide onboarding based on info from local storage
  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const native = params.get("native")
    const seenOnboarding = localStorage.getItem("seen-onb")
    const shouldShowOnboarding =
      seenOnboarding !== "true" && native ? true : false
    setShowOnboarding(shouldShowOnboarding)

    let cancelled = false
    if (!shouldShowOnboarding) {
      fetchUser().then((authUser) => {
        // Deep links (e.g. /rmdr) unmount Login; do not steal navigation after that.
        if (cancelled) return
        if (authUser?.role === finapiRole) {
          return navigate("/finapi/dashboard", { replace: true })
        } else if (userRoles.includes(authUser?.role)) {
          return navigate("/dashboard", { replace: true })
        } else {
          setPageShouldLoad(true)
        }
      })
    } else {
      setPageShouldLoad(true)
    }
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current as NodeJS.Timeout)
      }
    }
  }, [])

  const disableButtonFn = () => {
    let secs = 90
    intervalRef.current = setInterval(() => {
      secs -= 1
      if (secs < 1) {
        clearInterval(intervalRef.current as NodeJS.Timeout)
      }
      setDisableTimeout(secs)
    }, 1000)
  }

  const handleResetPassword = async (
    setFieldValue: Function,
    email: string
  ) => {
    const existingEmail: any = await verifyEmail({ email })
    if (!existingEmail?.data?.length) {
      setResetError(errorMessages[EMAIL_NOT_FOUND])
    } else {
      const { email, tenantid, uid } = existingEmail.data[0]
      await sendPasswordResetEmail({
        email,
        uid,
        tenantid,
      })
      setLoginError(undefined)
      setFieldValue("password", "")
      disableButtonFn()
      setModalOpen(false)
    }
  }

  async function loginFn({ email, password, google }: FormProps) {
    setLoading(true)

    let res: any

    const openProp = isMobileApp ? "_blank" : "_self"
    if (google) {
      window.open(
        `${process.env.REACT_APP_OAUTH_BASE_URL}/oauth/google-oauth-verify`,
        openProp
      )
    } else {
      res = await signInService({ email, password })
    }
    if (!res?.uid && !google) {
      setLoginError(errorMessages[WRONG_EMAIL_OR_PASSWORD])
    } else {
      // await fetchUser()
      if (userRoles.includes(res?.role)) {
        redirectAfterLogin("/dashboard", navigate)
      } else if (res?.role === finapiRole) {
        redirectAfterLogin("/finapi/dashboard", navigate)
      }
    }

    setLoading(false)
  }

  const handleOauthLogin = async () => {
    await loginFn({ google: true })
  }

  return (
    <>
      {pageShouldLoad ? (
        <ExternalPage showOnboarding={showOnboarding} presenter="general">
          <Stack spacing={4}>
            <PageTitle>Log in</PageTitle>
            <Formik
              initialValues={{ email: "", password: "" }}
              onSubmit={(values) => loginFn(values)}
              validateOnBlur={false}
              validationSchema={Yup.object({
                email: Yup.string()
                  .email("Invalid email format")
                  .required("Email address is required"),
                password: Yup.string()
                  .min(6, "Password too short")
                  .required("Password is required"),
              })}
            >
              {(formik) => (
                <Form onSubmit={formik.handleSubmit}>
                  <FormControl fullWidth>
                    <FormLabel>
                      Email
                      <TextField
                        type="email"
                        color="primary"
                        name="email"
                        onChange={(e: any) => {
                          setLoginError(errorMessages[NONE])
                          setResetError(undefined)
                          formik.setFieldValue("email", e.target.value)
                        }}
                        value={formik.values.email}
                      />
                      <ErrorMessage component={ErrorBox} name="email" />
                    </FormLabel>
                    <FormLabel>
                      <PasswordInput
                        formlabel="Password"
                        name="password"
                        onChange={(e: any) => {
                          setLoginError(errorMessages[NONE])
                          setResetError(undefined)
                          formik.setFieldValue("password", e.target.value)
                        }}
                        value={formik.values.password}
                      />
                      <ErrorMessage component={ErrorBox} name="password" />
                    </FormLabel>
                    <LoadingButton
                      type="submit"
                      color="primary"
                      variant="contained"
                      loading={loading}
                      data-testid="loginCta"
                    >
                      Login
                    </LoadingButton>
                    {!isMobileApp && (
                      <LoadingButton
                        variant="outlined"
                        type="button"
                        loading={loading}
                        startIcon={<GoogleIcon />}
                        onClick={handleOauthLogin}
                        data-testid="googleLoginCta"
                      >
                        Login with Google
                      </LoadingButton>
                    )}
                    {loginError &&
                    !formik.errors.password &&
                    !formik.errors.email ? (
                      <>
                        <ErrorBox>{loginError?.message}</ErrorBox>
                        {loginError.code === WRONG_EMAIL_OR_PASSWORD &&
                        formik.values.password &&
                        !formik.errors.email ? (
                          <>
                            <Button
                              variant="outlined"
                              disabled={disableTimeout > 0}
                              onClick={() => setModalOpen(true)}
                            >
                              Reset Password
                              {disableTimeout > 0 ? (
                                <>{`  in ${disableTimeout}s`}</>
                              ) : null}
                            </Button>
                          </>
                        ) : null}
                      </>
                    ) : null}
                  </FormControl>
                  {modalOpen ? (
                    <Modal open={modalOpen}>
                      <Stack spacing={4}>
                        <PageTitle>Reset Password?</PageTitle>
                        <MarkDown>
                          This will send a password reset link to your email, to
                          continue please click on **Send Link** and check your
                          email for the link
                        </MarkDown>
                        <Stack gap={1}>
                          {resetError ? (
                            <ErrorBox>{resetError?.message}</ErrorBox>
                          ) : (
                            <Button
                              variant="contained"
                              fullWidth
                              data-testid="resetCta"
                              disabled={disableTimeout > 0}
                              onClick={() =>
                                handleResetPassword(
                                  formik.setFieldValue,
                                  formik.values.email
                                )
                              }
                            >
                              Send Link
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setModalOpen(false)}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Stack>
                    </Modal>
                  ) : null}
                </Form>
              )}
            </Formik>

            <Divider variant="fullWidth" />
            <Stack spacing={3} alignItems={"center"}>
              <Typography>Don't have an account?</Typography>
              {!isMobileApp && (
                <LoadingButton
                  variant="outlined"
                  type="button"
                  loading={loading}
                  startIcon={<GoogleIcon />}
                  onClick={handleOauthLogin}
                  data-testid="googleSignupCta"
                  fullWidth
                >
                  Sign up with Google
                </LoadingButton>
              )}

              <Button
                data-testid="signupCta"
                onClick={() => navigate("/signup")}
                color="primary"
                variant="outlined"
                fullWidth
              >
                Sign up with email
              </Button>
            </Stack>
          </Stack>
        </ExternalPage>
      ) : null}
    </>
  )
}

export default Login
